import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Package, 
  Grid3X3, 
  FileText, 
  MousePointerClick, 
  TrendingUp, 
  Clock,
  Users,
  Store,
  ArrowUpRight,
  ArrowDownRight,
  ExternalLink,
  Zap,
  BarChart3,
  LinkIcon,
  Loader2,
  AlertTriangle,
  CheckCircle2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import AdminLayout from '@/components/admin/AdminLayout';
import { dashboardAPI } from '@/lib/api';

// Mini chart component
const MiniChart = ({ data, color = '#F97316' }) => {
  if (!data || data.length === 0) return null;
  
  const max = Math.max(...data.map(d => d.clicks), 1);
  const width = 100;
  const height = 40;
  const padding = 2;
  
  const points = data.map((d, i) => {
    const x = padding + (i / (data.length - 1)) * (width - padding * 2);
    const y = height - padding - (d.clicks / max) * (height - padding * 2);
    return `${x},${y}`;
  }).join(' ');

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-10">
      <polyline
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        points={points}
      />
      <polyline
        fill={`${color}20`}
        stroke="none"
        points={`${padding},${height - padding} ${points} ${width - padding},${height - padding}`}
      />
    </svg>
  );
};

const StatCard = ({ title, value, icon: Icon, color, trend, trendValue, subtitle }) => (
  <Card className="relative overflow-hidden border-slate-200 hover:border-slate-300 transition-colors">
    <CardContent className="p-5">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-slate-500 mb-1">{title}</p>
          <p className="text-2xl font-bold text-slate-900 font-heading">{value}</p>
          {subtitle && (
            <p className="text-xs text-slate-400 mt-1">{subtitle}</p>
          )}
          {trend && (
            <div className={`flex items-center gap-1 mt-2 text-xs font-medium ${
              trend === 'up' ? 'text-green-600' : 'text-red-500'
            }`}>
              {trend === 'up' ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
              <span>{trendValue}</span>
            </div>
          )}
        </div>
        <div className={`p-3 rounded-xl ${color}`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
      </div>
    </CardContent>
  </Card>
);

const QuickAction = ({ href, icon: Icon, label, description }) => (
  <Link
    to={href}
    className="flex items-center gap-4 p-4 bg-white rounded-xl border border-slate-200 hover:border-orange-200 hover:shadow-md transition-all duration-200 group"
  >
    <div className="p-3 bg-orange-50 rounded-xl group-hover:bg-orange-100 transition-colors">
      <Icon className="w-5 h-5 text-orange-600" />
    </div>
    <div className="flex-1">
      <p className="font-medium text-slate-900 group-hover:text-orange-600 transition-colors">{label}</p>
      <p className="text-xs text-slate-500">{description}</p>
    </div>
    <ExternalLink className="w-4 h-4 text-slate-300 group-hover:text-orange-500 transition-colors" />
  </Link>
);

const TopProduct = ({ product, index }) => (
  <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
    <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center text-white font-bold text-sm">
      {index + 1}
    </div>
    {product.image_url && (
      <img 
        src={product.image_url} 
        alt={product.name}
        className="w-10 h-10 rounded-lg object-cover"
      />
    )}
    <div className="flex-1 min-w-0">
      <p className="text-sm font-medium text-slate-900 truncate">{product.name}</p>
      <p className="text-xs text-slate-500">{product.click_count || 0} cliques</p>
    </div>
  </div>
);

export const AdminDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [linkCheckResults, setLinkCheckResults] = useState(null);
  const [checkingLinks, setCheckingLinks] = useState(false);
  const [stats, setStats] = useState({
    total_products: 0,
    active_products: 0,
    total_offers: 0,
    expired_offers: 0,
    total_categories: 0,
    total_platforms: 0,
    total_blog_posts: 0,
    total_leads: 0,
    total_clicks: 0,
    clicks_today: 0,
    leads_today: 0,
    top_products: [],
    clicks_by_day: []
  });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await dashboardAPI.getStats();
        setStats(response.data);
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const handleCheckLinks = async () => {
    setCheckingLinks(true);
    try {
      const token = localStorage.getItem('carvalho_token');
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${API_URL}/api/admin/check-links`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setLinkCheckResults(data);
    } catch (e) {
      console.error(e);
    } finally {
      setCheckingLinks(false);
    }
  };

  const LinkChecker = () => (
    <Card className="border-slate-200">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <LinkIcon className="w-4 h-4" /> Verificador de Links
          </CardTitle>
          <Button onClick={handleCheckLinks} disabled={checkingLinks} size="sm" className="bg-orange-500 hover:bg-orange-600" data-testid="check-links-btn">
            {checkingLinks ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Verificando...</> : 'Verificar Todos os Links'}
          </Button>
        </div>
      </CardHeader>
      {linkCheckResults && (
        <CardContent>
          <div className="flex gap-4 mb-4">
            <div className="flex items-center gap-2 text-green-600">
              <CheckCircle2 className="w-4 h-4" /> <span className="text-sm font-medium">{linkCheckResults.ok} OK</span>
            </div>
            <div className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-4 h-4" /> <span className="text-sm font-medium">{linkCheckResults.broken} Quebrados</span>
            </div>
          </div>
          {linkCheckResults.results.filter(r => r.status !== 'ok').length > 0 && (
            <div className="space-y-2">
              {linkCheckResults.results.filter(r => r.status !== 'ok').map(r => (
                <div key={r.id} className="flex items-center gap-3 p-2 bg-red-50 rounded-lg border border-red-100">
                  <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{r.name}</p>
                    <p className="text-xs text-slate-500 truncate">{r.link || 'Sem link'}</p>
                  </div>
                  <span className="text-xs font-medium text-red-600 bg-red-100 px-2 py-0.5 rounded">{r.status} {r.status_code > 0 ? `(${r.status_code})` : ''}</span>
                </div>
              ))}
            </div>
          )}
          {linkCheckResults.broken === 0 && (
            <p className="text-sm text-green-600 text-center py-2">Todos os links estao funcionando!</p>
          )}
        </CardContent>
      )}
    </Card>
  );

  return (
    <AdminLayout title="Dashboard">
      <div className="space-y-6">
        {/* Stats Grid - Performance Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Total de Produtos"
            value={loading ? '...' : stats.total_products}
            icon={Package}
            color="bg-blue-500"
            subtitle={`${stats.active_products} ativos`}
          />
          <StatCard
            title="Cliques Hoje"
            value={loading ? '...' : stats.clicks_today}
            icon={MousePointerClick}
            color="bg-orange-500"
            subtitle={`${stats.total_clicks.toLocaleString()} total`}
            trend={stats.clicks_today > 0 ? 'up' : undefined}
            trendValue={stats.clicks_today > 0 ? `+${stats.clicks_today} hoje` : undefined}
          />
          <StatCard
            title="Leads de WhatsApp"
            value={loading ? '...' : stats.total_leads}
            icon={Users}
            color="bg-green-500"
            trend={stats.leads_today > 0 ? 'up' : undefined}
            trendValue={stats.leads_today > 0 ? `+${stats.leads_today} hoje` : undefined}
          />
          <StatCard
            title="Loja Mais Clicada"
            value={loading ? '...' : (stats.store_most_clicks?.platform || '-')}
            icon={Store}
            color="bg-purple-500"
            subtitle={stats.store_most_clicks ? `${stats.store_most_clicks.total_clicks} cliques` : 'Sem dados'}
          />
        </div>

        {/* Secondary Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatCard
            title="Categorias"
            value={loading ? '...' : stats.total_categories}
            icon={Grid3X3}
            color="bg-indigo-500"
          />
          <StatCard
            title="Plataformas"
            value={loading ? '...' : stats.total_platforms}
            icon={Store}
            color="bg-pink-500"
          />
          <StatCard
            title="Posts do Blog"
            value={loading ? '...' : stats.total_blog_posts}
            icon={FileText}
            color="bg-cyan-500"
          />
        </div>

        {/* Charts & Top Products */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Most Clicked Product of the Week */}
          {stats.product_most_clicked_week && (
            <Card className="border-slate-200 lg:col-span-2 bg-gradient-to-r from-orange-50 to-amber-50">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Zap className="w-5 h-5 text-orange-500" />
                  <h3 className="font-semibold text-slate-900">Produto Mais Clicado da Semana</h3>
                </div>
                <div className="flex items-center gap-4">
                  {stats.product_most_clicked_week.image_url && (
                    <img
                      src={stats.product_most_clicked_week.image_url}
                      alt={stats.product_most_clicked_week.name}
                      className="w-16 h-16 rounded-xl object-cover bg-white"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-900 truncate">{stats.product_most_clicked_week.name}</p>
                    <p className="text-sm text-slate-500">Plataforma: {stats.product_most_clicked_week.platform}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-orange-600 font-heading">{stats.product_most_clicked_week.clicks_week}</p>
                    <p className="text-xs text-slate-500">cliques esta semana</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Clicks Line Chart */}
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-medium">Cliques nos Ultimos 7 Dias</CardTitle>
                <BarChart3 className="w-4 h-4 text-slate-400" />
              </div>
            </CardHeader>
            <CardContent>
              {stats.clicks_by_day && stats.clicks_by_day.length > 0 ? (
                <div>
                  {/* Line Chart SVG */}
                  <div className="relative h-40">
                    <svg viewBox="0 0 700 160" className="w-full h-full" preserveAspectRatio="none">
                      {(() => {
                        const data = stats.clicks_by_day;
                        const maxClicks = Math.max(...data.map(d => d.clicks), 1);
                        const padding = { top: 10, bottom: 30, left: 10, right: 10 };
                        const chartW = 700 - padding.left - padding.right;
                        const chartH = 160 - padding.top - padding.bottom;
                        const pts = data.map((d, i) => ({
                          x: padding.left + (i / (data.length - 1)) * chartW,
                          y: padding.top + chartH - (d.clicks / maxClicks) * chartH,
                        }));
                        const linePath = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');
                        const areaPath = `${linePath} L${pts[pts.length - 1].x},${padding.top + chartH} L${pts[0].x},${padding.top + chartH} Z`;
                        return (
                          <>
                            {/* Grid lines */}
                            {[0, 0.25, 0.5, 0.75, 1].map((pct, i) => (
                              <line key={i} x1={padding.left} x2={700 - padding.right} y1={padding.top + chartH * (1 - pct)} y2={padding.top + chartH * (1 - pct)} stroke="#e2e8f0" strokeWidth="1" strokeDasharray="4,4" />
                            ))}
                            {/* Area fill */}
                            <path d={areaPath} fill="url(#lineGrad)" />
                            {/* Line */}
                            <path d={linePath} fill="none" stroke="#F97316" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                            {/* Dots */}
                            {pts.map((p, i) => (
                              <g key={i}>
                                <circle cx={p.x} cy={p.y} r="4" fill="#F97316" stroke="white" strokeWidth="2" />
                                <text x={p.x} y={p.y - 10} textAnchor="middle" className="text-[10px]" fill="#64748b" fontSize="11" fontWeight="600">{data[i].clicks}</text>
                                <text x={p.x} y={padding.top + chartH + 18} textAnchor="middle" fill="#94a3b8" fontSize="10">{data[i].date}</text>
                              </g>
                            ))}
                            <defs>
                              <linearGradient id="lineGrad" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor="#F97316" stopOpacity="0.2" />
                                <stop offset="100%" stopColor="#F97316" stopOpacity="0" />
                              </linearGradient>
                            </defs>
                          </>
                        );
                      })()}
                    </svg>
                  </div>
                </div>
              ) : (
                <div className="h-40 flex items-center justify-center text-slate-400 text-sm">
                  Sem dados disponiveis
                </div>
              )}
            </CardContent>
          </Card>

          {/* Top Products */}
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-medium">Produtos Mais Clicados</CardTitle>
                <TrendingUp className="w-4 h-4 text-slate-400" />
              </div>
            </CardHeader>
            <CardContent>
              {stats.top_products && stats.top_products.length > 0 ? (
                <div className="space-y-2">
                  {stats.top_products.map((product, index) => (
                    <TopProduct key={product.id} product={product} index={index} />
                  ))}
                </div>
              ) : (
                <div className="h-40 flex items-center justify-center text-slate-400 text-sm">
                  Nenhum produto com cliques ainda
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Link Checker */}
        <LinkChecker />

        {/* Quick Actions */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-base font-medium">Ações Rápidas</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <QuickAction
                href="/admin/produtos"
                icon={Package}
                label="Novo Produto"
                description="Adicionar oferta"
              />
              <QuickAction
                href="/admin/categorias"
                icon={Grid3X3}
                label="Nova Categoria"
                description="Organizar produtos"
              />
              <QuickAction
                href="/admin/leads"
                icon={Users}
                label="Ver Leads"
                description="Exportar contatos"
              />
              <QuickAction
                href="/admin/blog"
                icon={FileText}
                label="Novo Post"
                description="Criar conteúdo"
              />
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
